'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import useBoopMinimal from '@/hooks/use-boop-minimal';

const LearnMoreButton = () => {
  const [isHovering, setIsHovering] = React.useState(false);
  const isBooped = useBoopMinimal(isHovering);

  return (
    <ButtonElem
      href="https://whimsy.joshwcomeau.com/"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <ButtonContent>
        <Background data-is-booped={isBooped} />
        <ButtonText>Check it out!</ButtonText>
      </ButtonContent>
    </ButtonElem>
  );
};

const ButtonElem = styled.a`
  position: relative;
  display: block;
  color: white;
  text-align: center;
  text-decoration: none;
  animation: enter var(--spring-easing) var(--spring-duration) both;
  animation-delay: 900ms;
  transition:
    --light-color var(--color-swap-duration),
    --dark-color var(--color-swap-duration),
    --highlight-color var(--color-swap-duration),
    --shadow-color var(--color-swap-duration);

  html[data-color-mode='dark'] & {
    --light-color: oklch(0.95 0.2 88.12);
    --dark-color: oklch(0.8 0.17 83.09);
    --highlight-color: hsl(50deg 100% 80%);
    --shadow-color: hsl(25deg 80% 20% / 0.5);
    --text-color: black;

    @property --light-color {
      syntax: '<color>';
      inherits: true;
      initial-value: oklch(0.87 0.2 88.12);
    }
    @property --dark-color {
      syntax: '<color>';
      inherits: true;
      initial-value: oklch(0.71 0.17 83.09);
    }
    @property --highlight-color {
      syntax: '<color>';
      inherits: true;
      initial-value: hsl(50deg 100% 80%);
    }
    @property --shadow-color {
      syntax: '<color>';
      inherits: true;
      initial-value: hsl(25deg 100% 40% / 0.5);
    }
  }
  html[data-color-mode='light'] & {
    --light-color: hsl(220deg 100% 75%);
    --dark-color: hsl(230deg 100% 50%);
    --highlight-color: hsl(230deg 100% 80%);
    --shadow-color: hsl(0deg 0% 0% / 0.25);
    --text-color: white;

    @property --light-color {
      syntax: '<color>';
      inherits: true;
      initial-value: hsl(220deg 100% 75%);
    }
    @property --dark-color {
      syntax: '<color>';
      inherits: true;
      initial-value: hsl(230deg 100% 50%);
    }
    @property --highlight-color {
      syntax: '<color>';
      inherits: true;
      initial-value: hsl(230deg 100% 80%);
    }
    @property --shadow-color {
      syntax: '<color>';
      inherits: true;
      initial-value: hsl(0deg 0% 0% / 0.25);
    }

    text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 48rem) {
    animation-delay: 300ms;
  }

  @keyframes enter {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 48rem) {
    margin-top: -24px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: fadeFromTransparent 500ms 600ms backwards;
  }
`;

const ButtonContent = styled.span`
  position: relative;
  display: block;
  padding: 1rem 3.5rem;
`;

const Background = styled.span`
  /* prettier-ignore */
  --spring-easing: linear(0, 0.011, 0.045 1.7%, 0.181 3.6%, 0.896 9.8%, 1.136 12.4%, 1.283 14.7%, 1.325,1.347 17.2%, 1.351, 1.347, 1.334, 1.313 20.6%, 1.249 22.6%, 1.052 27.4%,0.969 29.7%, 0.907 32.3%, 0.889, 0.879 35%, 0.878 36.7%, 0.89, 0.913 40.6%,0.981 45.3%, 1.01 47.6%, 1.032, 1.042 52.7%, 1.043 54.5%, 1.039 56.5%,0.997 65.4%, 0.989, 0.986 70.2% 74.4%, 1.001 83%, 1.005 87.3% 92%, 1);
  --spring-duration: 1.167s;

  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    var(--light-color),
    var(--dark-color)
  );
  border-radius: 1000px;
  box-shadow:
    inset 0px -4px 6px var(--shadow-color),
    inset 0px -8px 16px var(--shadow-color),
    inset 0px -16px 32px var(--shadow-color);
  filter: brightness(100%);
  /* Hide the blurry pseudo-element */
  overflow: hidden;
  transition:
    transform var(--spring-easing) var(--spring-duration),
    filter 250ms;

  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 5px;
    right: 20px;
    height: 6px;
    background: var(--highlight-color);
    border-radius: 50% 50% 0 0;
    filter: blur(5px);
  }

  &[data-is-booped='true'] {
    transform: scaleX(1.04);
  }
  ${ButtonElem}:hover & {
    filter: brightness(120%);
  }
`;

const ButtonText = styled.span`
  position: relative;
  display: block;
  font-size: 1.125rem;
  font-weight: var(--font-weight-medium);
  animation: fadeFromTransparent 500ms both;
  animation-delay: 1200ms;
  color: var(--text-color);
  transition: color var(--color-swap-duration);

  @media (max-width: 48rem) {
    animation-delay: 600ms;
  }
`;

export default LearnMoreButton;
